import type {ImageProcessingNodes as Nodes} from "@cm/image-processing-nodes"
import type {MaterialNode} from "@cm/material-nodes"
import {
    Bump,
    Color,
    Displacement,
    DistanceTexture,
    NormalMap,
    TexBrick,
    TexGradient,
    TexNoise,
    TextureFromRevision,
    TextureFromSet,
    TextureSet,
    TextureType as PlatformTextureType,
    TexVoronoi,
} from "@cm/material-nodes"
import {DataObjectAssignmentType} from "@cm/utils/data-object-assignment"
import type {TypedImageData} from "@cm/utils/typed-image-data"
import {isSupportedImageTransformNode, SUPPORTED_IMAGE_TRANSFORMS, transformChainInputs} from "#material-maps-exporter/core/transforms"
import type {SupportedImageTransformNode} from "#material-maps-exporter/core/transforms"

export {isSupportedImageTransformNode} from "#material-maps-exporter/core/transforms"

type KeyT = string | number | symbol
type _OmitDeep<T, U extends KeyT> = T extends {[key: KeyT]: unknown}
    ? {[K in keyof T as Exclude<K, U>]: T[K] extends (infer A)[] ? _OmitDeep<A, U>[] : _OmitDeep<T[K], U>}
    : T
export type NonNullableDeep<T> = T extends {
    [key: KeyT]: unknown
}
    ? NonNullable<{[K in keyof T]: NonNullableDeep<T[K]>}>
    : NonNullable<T>
export type RequiredDeep<T> = T extends {[key: KeyT]: unknown} ? {[K in keyof T]-?: RequiredDeep<T[K]>} : T

type MaterialMapsExporterLogger = {
    debug: (message: string, ...additionalData: unknown[]) => void
    warn: (message: string, ...additionalData: unknown[]) => void
}

export let materialMapsExporterLogger: MaterialMapsExporterLogger | undefined

export function setMaterialMapsExporterLogger(logger: MaterialMapsExporterLogger | undefined) {
    materialMapsExporterLogger = logger
}

export const CM_TO_INCH = 0.393701

export const formats = ["exr", "tiff", "png", "jpeg"] as const
export type Format = (typeof formats)[number]

export const resolutions = ["original", "dpi72"] as const
export type Resolution = (typeof resolutions)[number]

export const IMG_FORMAT_TO_ENCODER_FN: {[key in Format]: string} = {
    exr: "image/x-exr",
    tiff: "image/tiff",
    png: "image/png",
    jpeg: "image/jpeg",
} as const

export type LegacyId = number
export type IdDetails = {id: string} | {legacyId: LegacyId}
export type MaterialDetails = {
    id: string
    legacyId: number
    articleId?: string | null | undefined
    name: string
    organization: {legacyId: number; name: string}
    tagAssignments: Array<{tag: {name: string}}>
}

export const DataObjectAssignmentTypeStringToNumber = {
    [DataObjectAssignmentType.MaterialMapsExport]: 171,
} as const

const supportedChainHeadNodes = [Color, TextureFromRevision, TextureSet, TextureFromSet] as const
type SupportedChainHeadNode = InstanceType<(typeof supportedChainHeadNodes)[number]>

/**
 * Nodes that sit between a texture and whatever consumes it. The exporter walks through them rather
 * than applying them: the map an adapter forwards is the map the export carries, and what the adapter
 * makes of it afterwards is the renderer's business. Each names the input to continue the chain through.
 */
const supportedChainAdapters = [
    [NormalMap, "color"],
    [Bump, "height"],
    [Displacement, "height"],
] as const

type SupportedChainAdapterNode = InstanceType<(typeof supportedChainAdapters)[number][0]>

export const isSupportedChainAdapterNode = (node: MaterialNode): node is SupportedChainAdapterNode =>
    supportedChainAdapters.some(([nodeClass]) => node instanceof nodeClass)

/**
 * Nodes that compute their image from a function rather than reference one. Nothing about them is
 * malformed — the exporter simply has no file to carry, so a channel fed by one exports empty. That
 * is a different thing from a graph the exporter cannot read, and the two are reported differently.
 */
const proceduralTextureNodes = [TexNoise, TexVoronoi, TexBrick, TexGradient, DistanceTexture] as const

export const isProceduralTextureNode = (node: MaterialNode): boolean => proceduralTextureNodes.some((nodeClass) => node instanceof nodeClass)

/** The inputs a chain may continue through (W-6): an adapter's own, a transform's candidates, `color` otherwise. */
export const chainInputSockets = (node: MaterialNode): readonly string[] => {
    const adapterInput = supportedChainAdapters.find(([nodeClass]) => node instanceof nodeClass)?.[1]
    return adapterInput !== undefined ? [adapterInput] : (transformChainInputs(node) ?? ["color"])
}

type SupportedLinearImageOperatorSequenceNode = SupportedImageTransformNode | SupportedChainHeadNode | SupportedChainAdapterNode
export const isSupportedLinearImageOperatorSequenceNode = (node: MaterialNode): node is SupportedLinearImageOperatorSequenceNode => {
    return isSupportedImageTransformNode(node) || supportedChainHeadNodes.some((nodeClass) => node instanceof nodeClass) || isSupportedChainAdapterNode(node)
}

/**
 * What a chain may be made of, labelled as the editor labels those nodes, so a UI explaining the
 * constraint cannot drift from the vocabulary enforcing it. Lazy: the labels come from each class's
 * definition, which must not be read while the modules are still initialising.
 */
export const supportedChainNodeLabels = (): {heads: string[]; transforms: string[]; adapters: string[]} => {
    const labelOf = (nodeClass: {prototype: {definition: {nodeClass: string; ui?: {label?: string}}}}) =>
        nodeClass.prototype.definition.ui?.label ?? nodeClass.prototype.definition.nodeClass
    return {
        heads: supportedChainHeadNodes.map(labelOf),
        transforms: SUPPORTED_IMAGE_TRANSFORMS.map(({nodeClass}) => labelOf(nodeClass)),
        adapters: supportedChainAdapters.map(([nodeClass]) => labelOf(nodeClass)),
    }
}

export const PLATFORM_TEXTURE_TYPE_TO_CHANNEL_LAYOUT: {[type: string]: TypedImageData["channelLayout"]} = Object.fromEntries([
    [PlatformTextureType.Anisotropy, "RGB"],
    [PlatformTextureType.AnisotropyRotation, "L"],
    [PlatformTextureType.AnisotropyStrength, "L"],
    [PlatformTextureType.Diffuse, "RGB"],
    [PlatformTextureType.Displacement, "L"],
    [PlatformTextureType.F0, "RGB"],
    [PlatformTextureType.Metalness, "L"],
    [PlatformTextureType.Normal, "RGB"],
    [PlatformTextureType.Roughness, "L"],
    [PlatformTextureType.SpecularStrength, "L"],
    [PlatformTextureType.Mask, "L"],
    [PlatformTextureType.Transmission, "RGB"],
])

type CanonicalTextureTypes = ["diffuse", "normal", "roughness", "specular-strength", "metalness", "anisotropy", "displacement", "transmission", "alpha"]
export type MapsNodes<TS extends Readonly<Array<string>>, N = Nodes.ImageNode> = {[type in TS[number]]: N}
export type MapsNodesForCanonicalTextureTypes = MapsNodes<CanonicalTextureTypes>

export type InputMapsNodes = {[type: string]: Nodes.ImageNode}

export type InputMapsInfo = {
    [type: string]: {
        data:
            | {
                  type: "DataObject"
                  dataObjectId: number
                  width: number
                  height: number
                  physicalWidth: number
                  physicalHeight: number
                  displacementScale?: number
                  transmissionMin?: number
                  transmissionMax?: number
              }
            | {type: "ShaderNodeRGB"; shaderNodeRGB: Color}
        transformations: MaterialNode[]
    }
}

export type ImageMapInfo = {
    width: number
    height: number
    physicalWidth: number
    physicalHeight: number
    displacementScale?: number
    textureType: string
}

export type EncodedConvertedNode = Nodes.Encode

export type TextureRevisionInfo = {
    type: "textureRevisionInfo"
    textureType: string
    revisionId: number
}

export type SourceInfo = {
    type: "sourceInfo"
    source: "materialRevision" | "textureSet"
    sourceId: number
    textureRevisionInfo: TextureRevisionInfo[] | undefined
}

export const normalsY = ["default", "y+up", "y+down"] as const
export type NormalY = (typeof normalsY)[number]
